import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  // Box,
  TextField,
  // Button,
  Typography,
  // Card,
  CardContent,
  // CircularProgress,
  Chip,
  Snackbar,
  // Alert,
  InputAdornment,
  // IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";

import { colors, space, radius, borders, fontSize, fontWeight, warning } from "../../design/tokens";

import {
  // Screen,
  // Content,
  Card,
  TextInput,
  Button,
  Text,
  // Select,
  Row, 
  // Grid,
  Alert,
  CircularProgress,
  Box,
  IconButton
} from "../layout";

import { IconX, IconCheck, IconSearch, IconPlus, IconClear} from "@tabler/icons-react";


import { addFood } from "../../store/foodsSlice";

// ─── Config ───────────────────────────────────────────────────────────────────

const USDA_KEY =
  import.meta.env.USDA_KEY ||
  process.env.REACT_APP_USDA_KEY ||
  "DEMO_KEY";

// ─── USDA product → your food schema ─────────────────────────────────────────

function mapUSDAFoodToFood(item) {

  const getCalories = () =>
    item.foodNutrients?.find(
      (n) => n.nutrientId === 1008 || 
            (n.nutrientName === "Energy" && n.unitName === "KCAL")
    )?.value ?? 0;
  const get = (name) =>
    item.foodNutrients?.find((n) => n.nutrientName === name)?.value ?? 0;

  const calories = Math.round(getCalories())
  const protein  = Math.round(get("Protein") * 10) / 10;
  const carbs    = Math.round(get("Carbohydrate, by difference") * 10) / 10;
  const fat      = Math.round(get("Total lipid (fat)") * 10) / 10;
  const fiber    = Math.round(get("Fiber, total dietary") * 10) / 10;
  const sugar    = Math.round(get("Sugars, total including NLEA") * 10) / 10;

  const desc = (item.description ?? "").toLowerCase();

  let categorie = "other";
  if (desc.includes("beef") || desc.includes("pork") || desc.includes("chicken") ||
      desc.includes("lamb") || desc.includes("turkey") || desc.includes("veal"))
    categorie = "meat";
  else if (desc.includes("fish") || desc.includes("salmon") || desc.includes("tuna") ||
           desc.includes("shrimp") || desc.includes("seafood") || desc.includes("cod"))
    categorie = "fish";
  else if (desc.includes("milk") || desc.includes("cheese") || desc.includes("yogurt") ||
           desc.includes("butter") || desc.includes("cream") || desc.includes("dairy"))
    categorie = "dairy";
  else if (desc.includes("vegetable") || desc.includes("broccoli") || desc.includes("carrot") ||
           desc.includes("spinach") || desc.includes("lettuce") || desc.includes("potato") ||
           desc.includes("tomato") || desc.includes("onion") || desc.includes("pepper"))
    categorie = "vegetable";
  else if (desc.includes("fruit") || desc.includes("apple") || desc.includes("banana") ||
           desc.includes("berry") || desc.includes("orange") || desc.includes("grape") ||
           desc.includes("pear") || desc.includes("mango") || desc.includes("pineapple"))
    categorie = "fruit";
  else if (desc.includes("bread") || desc.includes("grain") || desc.includes("rice") ||
           desc.includes("oat") || desc.includes("wheat") || desc.includes("pasta") ||
           desc.includes("flour") || desc.includes("cereal"))
    categorie = "grain";
  else if (desc.includes("juice") || desc.includes("drink") || desc.includes("beverage") ||
           desc.includes("water") || desc.includes("tea") || desc.includes("coffee"))
    categorie = "drink";
  else if (desc.includes("chocolate") || desc.includes("candy") || desc.includes("cookie") ||
           desc.includes("cake") || desc.includes("sugar") || desc.includes("sweet"))
    categorie = "sweet";
  else if (desc.includes("tofu") || desc.includes("soy") || desc.includes("lentil") ||
           desc.includes("bean") || desc.includes("chickpea") || desc.includes("tempeh"))
    categorie = "vegan";

  return {
    id: crypto.randomUUID(),
    fdcId: item.fdcId,
    name: item.description ?? "Unknown food",
    calories,
    protein,
    carbs,
    fat,
    fiber,
    sugar,
    categorie,
    servingSize: 100,
    unit: "g",
    barcode: null,
    source: "usda",
    dataType: item.dataType ?? null,
    imageUrl: null,
  };
}

// ─── USDA search ──────────────────────────────────────────────────────────────

async function searchUSDA(term, signal) {
  const url =
    `https://api.nal.usda.gov/fdc/v1/foods/search` +
    `?query=${encodeURIComponent(term)}` +
    `&dataType=Foundation` +
    `&pageSize=25` +
    `&api_key=${USDA_KEY}`;

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`usda_${res.status}`);
  const data = await res.json();
  // return data.foods ?? [];

  const raw = data.foods ?? [];

  // Keep only items that don't contain processing words
  const filtered = raw.filter(item => {
    const name = item.description.toLowerCase();
    return !name.includes("juice") &&
          !name.includes("sauce") &&
          !name.includes("dried") &&
          !name.includes("canned") &&
          !name.includes("frozen") &&
          !name.includes("flavored") &&
          !name.includes("drink") &&
          !name.includes("extract") &&
          !name.includes("powder") &&
          !name.includes("product");
  });

return filtered;
}

// ─── Result card ──────────────────────────────────────────────────────────────

function USDAResultCard({ item, onAdd, added }) {
  const food = mapUSDAFoodToFood(item);
  const hasNutrition = food.calories > 0 || food.protein > 0;

  return (
    <Card style={{ marginBottom: space.md, padding: space.xs, border: borders.sm, borderRadius: radius.sm, borderColor: colors.textMuted }}>
        <Box style={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "space-between"}}>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Text style={{fontSize: fontSize.md, fontWeight: fontWeight.bold, }}>
              {food.name}
            </Text>

            {hasNutrition ? (
              <Text style={{fontSize: fontSize.xs, fontWeight: fontWeight.regular, }}>
                {food.calories} kcal · {food.protein}g P · {food.carbs}g C · {food.fat}g F
                {food.fiber > 0 && ` · ${food.fiber}g fiber`} /100g
                {/* <Text style={{fontSize: fontSize.xs, fontWeight: fontWeight.regular, }}> /100g</Text> */}
              </Text>
            ) : (
              <Text style={{fontSize: fontSize.md, fontWeight: fontWeight.regular, }}>No nutrition data</Text>
            )}

            <Text style={{display:"inline-block",fontSize: fontSize.xs, fontWeight: fontWeight.bold, lineHeight: "1", padding: space.xs, background: colors.primaryDark, borderRadius:radius.xs}}>{food.categorie}</Text>
          </Box>

          <IconButton
            color={added ? "red" : "blue"}
            onClick={() => onAdd(food)}
            disabled={added}
            style={{ flexShrink: 0, alignSelf: "center", color: added ? "red" : "blue", }}
          >
            {added ? <IconCheck style={{lineHeight: "1", fontsize: fontSize.md}}/> : <IconPlus style={{lineHeight: "1", fontsize: fontSize.md}}/>}
          </IconButton>
        </Box>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function USDAFoodSearch() {
  const dispatch = useDispatch();

  const [query, setQuery]           = useState("");
  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [searched, setSearched]     = useState(false);
  const [error, setError]           = useState(null);
  const [addedIds, setAddedIds]     = useState(new Set());
  const [toast, setToast]           = useState(null);
  const abortRef                    = useRef(null);

  const search = async (q = query) => {
    const term = q.trim();
    if (!term) return;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setResults([]);
    setSearched(false);

    try {
      const foods = await searchUSDA(term, abortRef.current.signal);
      setResults(foods);
      setSearched(true);
    } catch (err) {
      if (err.name === "AbortError") return;
      if (err.message.includes("403")) {
        setError("Invalid API key — check your VITE_USDA_KEY environment variable.");
      } else if (err.message.includes("429")) {
        setError("Rate limit reached — please wait a moment and try again.");
      } else {
        setError("Search failed. Check your internet connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (food) => {
    console.log("food: " + food)
    dispatch(addFood(food));
    setAddedIds((prev) => new Set([...prev, food.fdcId]));
    setToast(`"${food.name}" added to library`);
  };

  const isAdded = (item) => addedIds.has(item.fdcId);

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setSearched(false);
    setError(null);
  };

  return (
    <Box >

      <Row flex={[4,1]} style={{ display: "flex",  alignItems:"center", width:"100%", gap:space.xs, marginBottom: space.md, }}>
       <Box style={{ position:"relative", width: "100%", display: "block",}}>
        <TextInput
          
          placeholder='Search e.g. "apple", "chicken breast", "greek yogurt"'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        {query && (
          <IconButton
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <IconX size={18} />
          </IconButton>
        )}
        </Box>

        <Button
          onClick={() => search()}
          disabled={loading || !query.trim()}
          
          style={{ display:"inline-flex", justifyContent: "center",whiteSpace: "nowrap", minWidth: "90px", background:colors.primary }}
        >
          {loading ? <CircularProgress size={24} /> : "Search"}
        </Button>
      </Row>

      {error && (
        <Alert style={{ marginBottom: space.xs, fontSize: "13px", color:colors.warning}}>{error}</Alert>
      )}

      {searched && !loading && results.length === 0 && !error && (
        <Text style={{color:colors.text, fontSize:fontSize.sm, }}>
          No foods found for "{query}". Try a different term.
        </Text>
      )}

      {results.length > 0 && (
        <>
          <Text style={{color:colors.text, fontSize:fontSize.sm, }}>
            {results.length} results — all values per 100g
          </Text>
          {results.map((item, i) => (
            <USDAResultCard
              key={item.fdcId ?? i}
              item={item}
              onAdd={handleAdd}
              added={isAdded(item)}
            />
          ))}
          <Text style={{color:colors.text, fontSize:fontSize.sm, }}>
            Data from{" "}
            <a href="https://fdc.nal.usda.gov" target="_blank" rel="noopener noreferrer">
              USDA FoodData Central
            </a>
            {" "}· Public domain
          </Text>
        </>
      )}
      
      {toast && (<Alert style={{marginTop: space.lg}}>{toast}</Alert>)}


    </Box>
  );
}
